import React, { Component, Fragment } from "react";
import cx from "classnames";
import { Col, Label, Row } from "reactstrap";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { AlertTriangle, Check, Pencil, RotateCw, X } from "lucide-react";
import ExtendedDropdown from "../../components/ExtendedDropdown";
import { FLAG_HIGHLIGHT_BACKGROUND, FLAG_HIGHLIGHT_BORDER_COLOR } from "../Kyc/components/flagSectionStyles";
import { SelectItem } from "../../models/SelectItem";
import { ResidencePermit, ResidencePermitType, ResidencePermitResponse, SaveResidencePermitRequest, residencePermitTypeOptions } from "../../models/ResidencePermit";
import { isKYCAssessAllowed, isKYCApproveAllowed, isKycSubmissionAllAllowed, isCRMAllAllowed, isSuperAdmin } from "../../authGuard";
import { userApiService } from "../../services/api/users/UsersApiService";
import { toastService } from "../../services/toastService";
import { utils, countryCodeToFlag } from "../../utils/utils";
import {
    EMPTY_RESIDENCE_PERMIT_DRAFT,
    ISSUING_COUNTRIES_UNAVAILABLE_MESSAGE,
    MAX_FOREIGNER_PERSONAL_NUMBER_LENGTH,
    MAX_ISSUING_AUTHORITY_LENGTH,
    MAX_RESIDENCE_PERMIT_REASON_LENGTH,
    MIN_PERMIT_DATE_OF_ISSUE,
    MIN_RESIDENCE_PERMIT_REASON_LENGTH,
    NO_RESIDENCE_PERMIT_MESSAGE,
    RESIDENCE_PERMIT_LOAD_FAILED_MESSAGE,
    RESIDENCE_PERMIT_LOAD_FAILED_TOAST,
    RESIDENCE_PERMIT_LOADING_MESSAGE,
    RESIDENCE_PERMIT_SAVE_FAILED_TOAST,
    ResidencePermitDraft,
    ResidencePermitErrors,
    formatDateOnly,
    fromDateOnlyString,
    isResidencePermitApprovalBlocked,
    residencePermitFailureMessage,
    startOfToday,
    toDateOnlyString,
    validateResidencePermitDraft,
} from "./residencePermitRules";

// The edit dialogue's toasts (validation, CRM's save errors, "no changes") stay 12 s instead of the
// portal's 3 s: CRM's 409 reasons are full sentences that 3 s is too short to read.
const RESIDENCE_PERMIT_TOAST_LIFE_MS = 12000;
// After this long the loading line says the GET is still running (its own timeout ends it in Retry).
const SLOW_LOAD_HINT_MS = 3000;
const ISSUING_COUNTRIES_NOT_LOADED_TOOLTIP = "The EU/EEA country list is not loaded. Use Retry under Issuing Country.";
// Same look as the Country of Registration labels, but a label longer than its column (e.g.
// "Foreigner's Personal Number") is cut with an ellipsis instead of running into the next one.
// Four per row, like the Main activity / Source of Funds row: 7 fields + the button make two
// aligned rows of four on a wide screen, and wrap to fewer per row on a narrow one.
const RESIDENCE_PERMIT_CELL_STYLE: React.CSSProperties = { flex: "1 1 calc(25% - 9px)", minWidth: "180px" };
const RESIDENCE_PERMIT_LABEL_STYLE: React.CSSProperties = { fontSize: "1em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block", maxWidth: "100%" };
const RESIDENCE_PERMIT_BOX_CLASS = "border border-solid border-[#d0d5dd] bg-[#fafafa] dark:border-neutral-600 dark:bg-transparent";
const RESIDENCE_PERMIT_BOX_STYLE: React.CSSProperties = { borderRadius: "8px", padding: "12px 14px", display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-start" };

interface ResidencePermitSectionProps {
    // The section's GET reads the individual; the save POST routes by the KYC submission (TNPDEVAPPS-1954).
    individualId: number;
    submissionId: number;
    // The banner text is about the scorecard's Approve action; the user-profile has no Approve, so it
    // passes false. Defaults to true.
    showApprovalBlockedBanner?: boolean;
    // Fires with every GET answer — on mount and after each save — null when the GET failed, so a
    // parent that gates an action on the permit (the scorecard's Approve lock) stays in step.
    onLoaded?: (response: ResidencePermitResponse | null) => void;
}

interface ResidencePermitSectionState {
    // The first GET has answered (successfully or not); until then nothing renders.
    isLoaded: boolean;
    // The last GET failed: the section shows a Retry instead of an empty permit it cannot edit.
    loadFailed: boolean;
    isLoadingResidencePermit: boolean;
    // Issuing Country options, loaded beside the GET and again when the editor opens with none.
    issuingCountries: SelectItem[];
    isLoadingIssuingCountries: boolean;
    issuingCountriesError: string;
    // The first GET is taking longer than SLOW_LOAD_HINT_MS: the loading line says it is still going.
    isSlowLoad: boolean;
    residencePermit: ResidencePermit | null;
    residencePermitRecords: ResidencePermit[];
    isMemberOfEuEea: boolean;
    residencePermitCanEdit: boolean;
    isSavingResidencePermit: boolean;
    // The save POST is taking longer than SLOW_LOAD_HINT_MS: the dialogue says it is still going.
    isSlowSave: boolean;
    isEditingResidencePermit: boolean;
    showDiscardResidencePermitDialog: boolean;
    residencePermitDraft: ResidencePermitDraft;
    residencePermitErrors: ResidencePermitErrors;
}

export default class ResidencePermitSection extends Component<ResidencePermitSectionProps, ResidencePermitSectionState> {
    validationErrors: Map<string, boolean> = new Map<string, boolean>();
    // Baseline the editor's draft is compared against: the current permit when the editor opened.
    originalResidencePermitDraft: ResidencePermitDraft = EMPTY_RESIDENCE_PERMIT_DRAFT;
    slowLoadTimer: ReturnType<typeof setTimeout> | undefined;
    slowSaveTimer: ReturnType<typeof setTimeout> | undefined;

    constructor(props: ResidencePermitSectionProps | Readonly<ResidencePermitSectionProps>) {
        super(props);

        this.state = {
            isLoaded: false,
            loadFailed: false,
            isLoadingResidencePermit: false,
            issuingCountries: [],
            isLoadingIssuingCountries: false,
            issuingCountriesError: "",
            isSlowLoad: false,
            residencePermit: null,
            residencePermitRecords: [],
            isMemberOfEuEea: false,
            residencePermitCanEdit: false,
            isSavingResidencePermit: false,
            isSlowSave: false,
            isEditingResidencePermit: false,
            showDiscardResidencePermitDialog: false,
            residencePermitDraft: EMPTY_RESIDENCE_PERMIT_DRAFT,
            residencePermitErrors: {},
        };
    }

    // The section shows as soon as its own GET answers. The Issuing Country list is only needed in the
    // editor, so it loads beside the GET and never holds the section back.
    componentDidMount = async () => {
        void this.loadIssuingCountries();
        this.slowLoadTimer = setTimeout(() => this.setState({ isSlowLoad: true }), SLOW_LOAD_HINT_MS);
        await this.loadResidencePermit(this.props.individualId);
        clearTimeout(this.slowLoadTimer);
        this.setState({ isLoaded: true });
    };

    componentWillUnmount() {
        clearTimeout(this.slowLoadTimer);
        clearTimeout(this.slowSaveTimer);
    }

    // Issuing Country options come from GET /country/eu-eea. Deliberately not fatal for the section:
    // the permit data still shows, and the editor says the list is missing, offers a Retry and keeps
    // "Done" disabled, because the required Issuing Country cannot be chosen without it. An empty
    // list counts as a failure (utils returns success:false with no message for it). utils does not
    // pass the HTTP status on, so a failure always shows the section's own message and the raw
    // text goes to the console (residencePermitFailureMessage).
    loadIssuingCountries = async () => {
        this.setState({ isLoadingIssuingCountries: true, issuingCountriesError: "" });
        const countriesResult = await utils.getEuEeaCountriesResult();
        const issuingCountries = countriesResult.success ? utils.getEuEeaCountries() : [];
        this.setState({
            issuingCountries,
            isLoadingIssuingCountries: false,
            issuingCountriesError: issuingCountries.length > 0 ? "" : residencePermitFailureMessage(countriesResult, ISSUING_COUNTRIES_UNAVAILABLE_MESSAGE),
        });
    };

    // TNPDEVAPPS-1953: who may open the Residence Permit editor. See docs/features/residence-permit-scorecard.md.
    canEditResidencePermit = (): boolean => isKYCAssessAllowed() || isKYCApproveAllowed() || isKycSubmissionAllAllowed() || isCRMAllAllowed() || isSuperAdmin();

    // GET /Individual/{id}/residence-permit returns the EU/EEA flag, whether the caller may edit,
    // and every recorded permit with the active one first (an empty list when there is none).
    // On failure the section keeps no permit and shows a Retry, and the parent gets null, so the
    // scorecard's Approve stays locked (fail closed) with a tooltip that says the data failed to load.
    loadResidencePermit = async (individualId: number) => {
        this.setState({ isLoadingResidencePermit: true });
        const result = await userApiService.getResidencePermit(individualId);
        if (!result.success) {
            toastService.error(residencePermitFailureMessage(result, RESIDENCE_PERMIT_LOAD_FAILED_TOAST));
        }
        const response = result.success ? result.data : null;
        const records = response?.residencePermitData ?? [];
        this.setState({
            loadFailed: response == null,
            isLoadingResidencePermit: false,
            residencePermitRecords: records,
            residencePermit: this.currentResidencePermit(records),
            isMemberOfEuEea: response?.isMemberOfEuEea === true,
            residencePermitCanEdit: response?.canEditResidencePermit === true,
            residencePermitErrors: {},
        });
        this.props.onLoaded?.(response);
    };

    // CRM lists the active permit first, else the most recently entered one; that first record is
    // the current permit, so a deactivated permit can still be shown and re-activated.
    currentResidencePermit = (records: ResidencePermit[]): ResidencePermit | null => records[0] ?? null;

    get residencePermitApprovalBlocked(): boolean {
        return isResidencePermitApprovalBlocked(this.state.isMemberOfEuEea, this.state.residencePermit);
    }

    residencePermitToDraft = (permit: ResidencePermit | null | undefined): ResidencePermitDraft => {
        if (!permit) {
            return EMPTY_RESIDENCE_PERMIT_DRAFT;
        }
        return {
            permitType: permit.permitType,
            foreignerPersonalNumber: permit.foreignerPersonalNumber ?? "",
            documentNumber: permit.documentNumber ?? "",
            issuingCountryCode: permit.issuingCountryCode ?? "",
            issuingAuthority: permit.issuingAuthority ?? "",
            dateOfIssue: fromDateOnlyString(permit.dateOfIssue),
            dateOfExpiry: permit.noExpirationDate ? null : fromDateOnlyString(permit.dateOfExpiry),
            noExpirationDate: permit.noExpirationDate,
            isActive: permit.isActive,
            // Never pre-filled: a change must carry its own new reason, not slip through on the
            // previous one. The stored reasons are shown in the history.
            reason: "",
        };
    };

    // The one change check for "Done" and "Cancel", over the same fields CRM's MatchesRequest
    // compares — the reason included, so a new reason alone is a change. Text is compared trimmed,
    // as it is sent.
    residencePermitChanged = (): boolean => {
        const draft = this.state.residencePermitDraft;
        const original = this.originalResidencePermitDraft;
        const sameDate = (a: Date | null, b: Date | null) => (a?.getTime() ?? null) === (b?.getTime() ?? null);
        return draft.permitType !== original.permitType
            || draft.foreignerPersonalNumber !== original.foreignerPersonalNumber
            || draft.documentNumber !== original.documentNumber
            || draft.issuingCountryCode !== original.issuingCountryCode
            || draft.issuingAuthority.trim() !== original.issuingAuthority.trim()
            || !sameDate(draft.dateOfIssue, original.dateOfIssue)
            || !sameDate(draft.dateOfExpiry, original.dateOfExpiry)
            || draft.noExpirationDate !== original.noExpirationDate
            || draft.isActive !== original.isActive
            || draft.reason.trim() !== original.reason.trim();
    };

    onOpenResidencePermitEditor = () => {
        const draft = this.residencePermitToDraft(this.state.residencePermit);
        this.originalResidencePermitDraft = draft;
        this.setState({ residencePermitDraft: draft, residencePermitErrors: {}, isEditingResidencePermit: true });
        // The list may have failed on mount; try again rather than open a dialogue that cannot be saved.
        if (this.state.issuingCountries.length === 0 && !this.state.isLoadingIssuingCountries) {
            void this.loadIssuingCountries();
        }
    };

    onResidencePermitFieldChange = <K extends keyof ResidencePermitDraft>(field: K, value: ResidencePermitDraft[K]) => {
        const residencePermitErrors = { ...this.state.residencePermitErrors };
        delete residencePermitErrors[field];
        this.setState({ residencePermitDraft: { ...this.state.residencePermitDraft, [field]: value }, residencePermitErrors });
    };

    onResidencePermitNoExpirationChange = (checked: boolean) => {
        const residencePermitErrors = { ...this.state.residencePermitErrors };
        delete residencePermitErrors.dateOfExpiry;
        this.setState({
            residencePermitDraft: {
                ...this.state.residencePermitDraft,
                noExpirationDate: checked,
                // Checked clears and locks Date of Expiry; it is sent as null.
                dateOfExpiry: checked ? null : this.state.residencePermitDraft.dateOfExpiry,
            },
            residencePermitErrors,
        });
    };

    // Runs on "Done", only when the permit data was actually changed. The rules live in
    // residencePermitRules.ts; this shows their result.
    validateResidencePermit = (): boolean => {
        const errors = validateResidencePermitDraft(this.state.residencePermitDraft);
        this.setState({ residencePermitErrors: errors });
        const failedFields = Object.keys(errors);
        if (failedFields.length > 0) {
            // Named like validateCountryOverride's toast; per-field messages stay under each field.
            toastService.error(failedFields.length === 1 && errors.reason
                ? "Please provide a reason for the residence permit change."
                : "Please correct the highlighted residence permit fields.", undefined, false, RESIDENCE_PERMIT_TOAST_LIFE_MS);
            return false;
        }
        return true;
    };

    // "Done" records the permit straight away: POST /Individual/{submissionId}/residence-permit
    // creates a new row (active or deactivated) and refreshes the submission's permit snapshot and
    // gate flag in the same call; the section is then re-read from GET. Without changes it closes.
    onDoneEditResidencePermit = async () => {
        if (!this.residencePermitChanged()) {
            this.setState({ isEditingResidencePermit: false, residencePermitErrors: {} });
            return;
        }
        if (!this.validateResidencePermit()) {
            return;
        }

        const draft = this.state.residencePermitDraft;
        const request: SaveResidencePermitRequest = {
            individualId: this.props.individualId,
            permitType: draft.permitType as ResidencePermitType,
            foreignerPersonalNumber: draft.foreignerPersonalNumber,
            documentNumber: draft.documentNumber,
            issuingCountryCode: draft.issuingCountryCode,
            issuingAuthority: draft.issuingAuthority.trim(),
            dateOfIssue: toDateOnlyString(draft.dateOfIssue as Date),
            dateOfExpiry: draft.noExpirationDate || !draft.dateOfExpiry ? null : toDateOnlyString(draft.dateOfExpiry),
            noExpirationDate: draft.noExpirationDate,
            isActive: draft.isActive,
            reason: draft.reason.trim(),
        };

        this.setState({ isSavingResidencePermit: true, isSlowSave: false });
        this.slowSaveTimer = setTimeout(() => this.setState({ isSlowSave: true }), SLOW_LOAD_HINT_MS);
        // A 409 carries CRM's reason (no Residence Permit scan on the submission, a closed
        // submission, an EU/EEA national); it is shown as the toast and the dialogue keeps the edit.
        const result = await userApiService.saveResidencePermit(this.props.submissionId, request);
        clearTimeout(this.slowSaveTimer);
        this.setState({ isSavingResidencePermit: false, isSlowSave: false });
        if (!result.success) {
            toastService.error(residencePermitFailureMessage(result, RESIDENCE_PERMIT_SAVE_FAILED_TOAST), undefined, false, RESIDENCE_PERMIT_TOAST_LIFE_MS);
            return;
        }
        if (result.data && !result.data.dataChanged) {
            toastService.info(result.data.message || "No changes to save.", undefined, RESIDENCE_PERMIT_TOAST_LIFE_MS);
        }

        this.setState({ isEditingResidencePermit: false });
        await this.loadResidencePermit(this.props.individualId);
    };

    onCancelEditResidencePermit = () => {
        if (this.state.isSavingResidencePermit) {
            return;
        }
        if (this.residencePermitChanged()) {
            this.setState({ showDiscardResidencePermitDialog: true });
            return;
        }
        this.setState({ isEditingResidencePermit: false, residencePermitErrors: {} });
    };

    onConfirmDiscardResidencePermit = () => {
        this.setState({
            residencePermitDraft: this.originalResidencePermitDraft,
            residencePermitErrors: {},
            isEditingResidencePermit: false,
            showDiscardResidencePermitDialog: false,
        });
    };

    renderResidencePermitError = (field: keyof ResidencePermitErrors): React.ReactNode =>
        this.state.residencePermitErrors[field] ? <small className="p-error block">{this.state.residencePermitErrors[field]}</small> : null;

    // PrimeReact passes the dropdown's props as the second argument. With nothing selected the
    // placeholder is shown, so the field keeps its height (an empty span collapsed it).
    selectedCountryTemplate = (option: SelectItem, props?: { placeholder?: string }): React.ReactNode => {
        if (option) {
            return (
                <div className="flex items-center gap-2">
                    <span className="text-base leading-none">{countryCodeToFlag(option.value)}</span>
                    <span>{option.label}</span>
                </div>
            );
        }
        return <span>{props?.placeholder || "\u00a0"}</span>;
    };

    countryOptionTemplate = (option: SelectItem): React.ReactNode => (
        <div className="flex items-center gap-2">
            <span className="text-base leading-none">{countryCodeToFlag(option.value)}</span>
            <span>{option.label}</span>
        </div>
    );

    render() {
        // While the first GET runs the box says so, since the scorecard's Approve is already locked and
        // waits on it. For an EU/EEA national it then disappears; a short loading line is the price of
        // never leaving the operator without a word.
        if (!this.state.isLoaded) {
            return (
                <Col sm={12} className="p-2.5">
                    <div className={RESIDENCE_PERMIT_BOX_CLASS} style={{ ...RESIDENCE_PERMIT_BOX_STYLE, alignItems: "center" }}>
                        <span style={{ fontWeight: 600 }}>Residence Permit</span>
                        <span role="status">{this.state.isSlowLoad ? "Still loading residence permit data…" : RESIDENCE_PERMIT_LOADING_MESSAGE}</span>
                    </div>
                </Col>
            );
        }

        // The GET failed: whether the client is EU/EEA and whether a permit exists are unknown, so show
        // why, and a way to try again, instead of an empty permit with a disabled button.
        if (this.state.loadFailed) {
            return (
                <Col sm={12} className="p-2.5">
                    <div className={RESIDENCE_PERMIT_BOX_CLASS} style={{ ...RESIDENCE_PERMIT_BOX_STYLE, alignItems: "center" }}>
                        <span style={{ fontWeight: 600 }}>Residence Permit</span>
                        <div style={{ flex: "1 1 auto", minWidth: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                            <AlertTriangle size={16} style={{ color: "#856404", flexShrink: 0 }} />
                            <span>{RESIDENCE_PERMIT_LOAD_FAILED_MESSAGE}.</span>
                        </div>
                        <Button
                            label="Retry"
                            type="button"
                            severity="info"
                            icon={<RotateCw size={14} />}
                            loading={this.state.isLoadingResidencePermit}
                            disabled={this.state.isLoadingResidencePermit}
                            onClick={() => this.loadResidencePermit(this.props.individualId)}
                        />
                    </div>
                </Col>
            );
        }

        // Hidden entirely for EU/EEA nationals once the GET says so: they need no residence permit.
        if (this.state.isMemberOfEuEea) {
            return null;
        }

        return (
            <Fragment>
                <Col sm={12} className="p-2.5">
                    {/* Colours as classes, not inline, so dark mode can override them; light mode is unchanged. */}
                    <div className={RESIDENCE_PERMIT_BOX_CLASS} style={RESIDENCE_PERMIT_BOX_STYLE}>
                        <div style={{ flex: "1 1 100%", minWidth: 0, display: "flex", alignItems: "center", gap: "16px" }}>
                            <span style={{ fontWeight: 600 }}>Residence Permit</span>
                            {/* Status of the whole current permit, read-only; unchecked when there is none. */}
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <Checkbox inputId="rpIsActiveView" checked={this.state.residencePermit?.isActive === true} disabled={true} />
                                <label htmlFor="rpIsActiveView" style={{ margin: 0, fontSize: "0.85em", color: "#666" }}>Active</label>
                            </div>
                        </div>

                        {/* Same rule as the Approve lock, so the banner never claims approval is blocked when it is not. */}
                        {(this.props.showApprovalBlockedBanner ?? true) && this.residencePermitApprovalBlocked && (
                            <div style={{ flex: "1 1 100%", minWidth: 0, background: FLAG_HIGHLIGHT_BACKGROUND, border: `1px solid ${FLAG_HIGHLIGHT_BORDER_COLOR}`, borderRadius: "6px", padding: "8px 12px", display: "flex", alignItems: "center", gap: "8px" }}>
                                <AlertTriangle size={16} style={{ color: "#856404", flexShrink: 0 }} />
                                <span style={{ color: "#856404", fontSize: "14px" }}>{NO_RESIDENCE_PERMIT_MESSAGE}</span>
                            </div>
                        )}

                        <div style={RESIDENCE_PERMIT_CELL_STYLE}>
                            <Label for="rpPermitType" title="Permit Type" style={RESIDENCE_PERMIT_LABEL_STYLE}>
                                Permit Type
                            </Label>
                            <InputText
                                value={residencePermitTypeOptions.find((o) => o.value === this.state.residencePermit?.permitType)?.label ?? ""}
                                className={"form-control"}
                                id="rpPermitType"
                                readOnly={true}
                            />
                        </div>

                        <div style={RESIDENCE_PERMIT_CELL_STYLE}>
                            <Label for="rpForeignerPersonalNumber" title="Foreigner's Personal Number" style={RESIDENCE_PERMIT_LABEL_STYLE}>
                                Foreigner's Personal Number
                            </Label>
                            <InputText
                                value={this.state.residencePermit?.foreignerPersonalNumber ?? ""}
                                className={"form-control"}
                                id="rpForeignerPersonalNumber"
                                readOnly={true}
                            />
                        </div>

                        <div style={RESIDENCE_PERMIT_CELL_STYLE}>
                            <Label for="rpDocumentNumber" title="Document Number" style={RESIDENCE_PERMIT_LABEL_STYLE}>
                                Document Number
                            </Label>
                            <InputText
                                value={this.state.residencePermit?.documentNumber ?? ""}
                                className={"form-control"}
                                id="rpDocumentNumber"
                                readOnly={true}
                            />
                        </div>

                        <div style={RESIDENCE_PERMIT_CELL_STYLE}>
                            <Label for="rpIssuingCountry" title="Issuing Country" style={RESIDENCE_PERMIT_LABEL_STYLE}>
                                Issuing Country
                            </Label>
                            <InputText
                                value={this.state.residencePermit ? utils.getCountryName(this.state.residencePermit.issuingCountryCode) ?? this.state.residencePermit.issuingCountryCode : ""}
                                className={"form-control"}
                                id="rpIssuingCountry"
                                readOnly={true}
                            />
                        </div>

                        <div style={RESIDENCE_PERMIT_CELL_STYLE}>
                            <Label for="rpIssuingAuthority" title="Issuing Authority" style={RESIDENCE_PERMIT_LABEL_STYLE}>
                                Issuing Authority
                            </Label>
                            <InputText
                                value={this.state.residencePermit?.issuingAuthority ?? ""}
                                className={"form-control"}
                                id="rpIssuingAuthority"
                                readOnly={true}
                            />
                        </div>

                        <div style={RESIDENCE_PERMIT_CELL_STYLE}>
                            <Label for="rpDateOfIssue" title="Date of Issue" style={RESIDENCE_PERMIT_LABEL_STYLE}>
                                Date of Issue
                            </Label>
                            <InputText
                                value={formatDateOnly(this.state.residencePermit?.dateOfIssue)}
                                className={"form-control"}
                                id="rpDateOfIssue"
                                readOnly={true}
                            />
                        </div>

                        <div style={RESIDENCE_PERMIT_CELL_STYLE}>
                            <Label for="rpDateOfExpiry" title="Date of Expiry" style={RESIDENCE_PERMIT_LABEL_STYLE}>
                                Date of Expiry
                            </Label>
                            <InputText
                                value={this.state.residencePermit?.noExpirationDate ? "No expiration date" : formatDateOnly(this.state.residencePermit?.dateOfExpiry)}
                                className={"form-control"}
                                id="rpDateOfExpiry"
                                readOnly={true}
                            />
                        </div>

                        {this.canEditResidencePermit() && (
                            <div style={RESIDENCE_PERMIT_CELL_STYLE}>
                                <label style={{ display: "block", visibility: "hidden", fontSize: "1em" }}>&nbsp;</label>
                                <Button
                                    label="Fill Residence Permit Data"
                                    type="button"
                                    severity="info"
                                    icon={<Pencil size={14} />}
                                    disabled={!this.state.residencePermitCanEdit}
                                    onClick={() => this.onOpenResidencePermitEditor()}
                                    style={{ whiteSpace: "nowrap" }}
                                />
                            </div>
                        )}

                        {this.canEditResidencePermit() && this.state.residencePermitRecords.length > 0 && (
                            <div style={{ flex: "1 1 100%", minWidth: 0, marginTop: "4px" }}>
                                <Label style={{ fontSize: "0.9em", color: "#666", whiteSpace: "nowrap" }}>
                                    Residence Permit History ({this.state.residencePermitRecords.length})
                                </Label>
                                {/* A row is usually one line, so ~100px shows two to three; a long reason wraps and fewer fit. The rest scrolls. */}
                                <div className="border border-solid border-[#e5e7eb] bg-white dark:border-neutral-700 dark:bg-neutral-900" style={{ maxHeight: "100px", overflowY: "auto", borderRadius: "6px" }}>
                                    {[...this.state.residencePermitRecords].sort((a, b) => b.id - a.id).map((entry) => (
                                        <div
                                            key={entry.id}
                                            className={cx("border-b border-solid border-[#f0f0f0] dark:border-neutral-800", !entry.isActive && "bg-[#f8f9fa] dark:bg-neutral-800")}
                                            style={{
                                                padding: "6px 10px",
                                                fontSize: "0.85em",
                                                display: "flex",
                                                gap: "10px",
                                                flexWrap: "wrap",
                                                color: entry.isActive ? undefined : "#666",
                                            }}
                                        >
                                            <span><strong>Document Number:</strong> {entry.documentNumber}</span>
                                            <span><strong>Issuing Country:</strong> {utils.getCountryName(entry.issuingCountryCode) ?? entry.issuingCountryCode}</span>
                                            <span><strong>Date of Expiry:</strong> {entry.noExpirationDate ? "No expiration" : formatDateOnly(entry.dateOfExpiry)}</span>
                                            {entry.reason && <span><strong>Reason:</strong> {entry.reason}</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Col>

                {/* Edit Residence Permit Dialog */}
                <Dialog
                    header="Residence Permit Data"
                    visible={this.state.isEditingResidencePermit}
                    style={{ width: "50vw" }}
                    modal
                    onHide={() => this.onCancelEditResidencePermit()}
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                                <Label for="rpPermitTypeEdit" style={{ fontSize: "1em", whiteSpace: "nowrap" }}>
                                    Permit Type
                                </Label>
                                <ExtendedDropdown
                                    value={this.state.residencePermitDraft.permitType}
                                    options={residencePermitTypeOptions}
                                    onChange={(e) => this.onResidencePermitFieldChange("permitType", e.value)}
                                    placeholder="Select a Permit Type"
                                    required={true}
                                    name="Permit Type"
                                    onValidStatusChanged={(value: boolean) => this.validationErrors.set("rpPermitType", value)}
                                />
                                {this.renderResidencePermitError("permitType")}
                            </div>

                            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                                <Label for="rpIssuingCountryEdit" style={{ fontSize: "1em", whiteSpace: "nowrap" }}>
                                    Issuing Country
                                </Label>
                                <ExtendedDropdown
                                    value={this.state.residencePermitDraft.issuingCountryCode}
                                    options={this.state.issuingCountries}
                                    onChange={(e) => this.onResidencePermitFieldChange("issuingCountryCode", e.value)}
                                    filter
                                    filterBy="label"
                                    placeholder="Select a Country"
                                    required={true}
                                    name="Issuing Country"
                                    onValidStatusChanged={(value: boolean) => this.validationErrors.set("rpIssuingCountryCode", value)}
                                    valueTemplate={this.selectedCountryTemplate}
                                    itemTemplate={this.countryOptionTemplate}
                                />
                                {this.renderResidencePermitError("issuingCountryCode")}
                                {this.state.isLoadingIssuingCountries && <small className="block">Loading countries…</small>}
                                {!this.state.isLoadingIssuingCountries && this.state.issuingCountriesError && (
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                                        <small className="p-error">{this.state.issuingCountriesError}</small>
                                        <Button
                                            label="Retry"
                                            type="button"
                                            severity="secondary"
                                            size="small"
                                            icon={<RotateCw size={14} />}
                                            onClick={() => this.loadIssuingCountries()}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                                <Label for="rpForeignerPersonalNumberEdit" style={{ fontSize: "1em", whiteSpace: "nowrap" }}>
                                    Foreigner's Personal Number
                                </Label>
                                <InputText
                                    value={this.state.residencePermitDraft.foreignerPersonalNumber}
                                    className={cx("form-control", this.state.residencePermitErrors.foreignerPersonalNumber ? "p-invalid" : "")}
                                    id="rpForeignerPersonalNumberEdit"
                                    maxLength={MAX_FOREIGNER_PERSONAL_NUMBER_LENGTH}
                                    onChange={(e) => this.onResidencePermitFieldChange("foreignerPersonalNumber", e.target.value.trim().toUpperCase())}
                                />
                                {this.renderResidencePermitError("foreignerPersonalNumber")}
                            </div>

                            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                                <Label for="rpDocumentNumberEdit" style={{ fontSize: "1em", whiteSpace: "nowrap" }}>
                                    Document Number
                                </Label>
                                <InputText
                                    value={this.state.residencePermitDraft.documentNumber}
                                    className={cx("form-control", this.state.residencePermitErrors.documentNumber ? "p-invalid" : "")}
                                    id="rpDocumentNumberEdit"
                                    maxLength={30}
                                    onChange={(e) => this.onResidencePermitFieldChange("documentNumber", e.target.value.trim())}
                                />
                                {this.renderResidencePermitError("documentNumber")}
                            </div>
                        </div>

                        <div>
                            <Label for="rpIssuingAuthorityEdit" style={{ fontSize: "1em", whiteSpace: "nowrap" }}>
                                Issuing Authority
                            </Label>
                            <InputText
                                value={this.state.residencePermitDraft.issuingAuthority}
                                className={cx("form-control", this.state.residencePermitErrors.issuingAuthority ? "p-invalid" : "")}
                                id="rpIssuingAuthorityEdit"
                                maxLength={MAX_ISSUING_AUTHORITY_LENGTH}
                                onChange={(e) => this.onResidencePermitFieldChange("issuingAuthority", e.target.value)}
                            />
                            {this.renderResidencePermitError("issuingAuthority")}
                        </div>

                        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-start" }}>
                            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                                <Label for="rpDateOfIssueEdit" style={{ fontSize: "1em", whiteSpace: "nowrap" }}>
                                    Date of Issue
                                </Label>
                                <Calendar
                                    inputId="rpDateOfIssueEdit"
                                    value={this.state.residencePermitDraft.dateOfIssue}
                                    onChange={(e) => this.onResidencePermitFieldChange("dateOfIssue", (e.value as Date | null) ?? null)}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    minDate={MIN_PERMIT_DATE_OF_ISSUE}
                                    maxDate={startOfToday()}
                                    appendTo={document.body}
                                    invalid={!!this.state.residencePermitErrors.dateOfIssue}
                                    style={{ width: "100%" }}
                                />
                                {this.renderResidencePermitError("dateOfIssue")}
                            </div>

                            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                                <Label for="rpDateOfExpiryEdit" style={{ fontSize: "1em", whiteSpace: "nowrap" }}>
                                    Date of Expiry
                                </Label>
                                <Calendar
                                    inputId="rpDateOfExpiryEdit"
                                    value={this.state.residencePermitDraft.dateOfExpiry}
                                    onChange={(e) => this.onResidencePermitFieldChange("dateOfExpiry", (e.value as Date | null) ?? null)}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    disabled={this.state.residencePermitDraft.noExpirationDate}
                                    placeholder="Required unless No expiration date"
                                    appendTo={document.body}
                                    invalid={!!this.state.residencePermitErrors.dateOfExpiry}
                                    style={{ width: "100%" }}
                                />
                                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px" }}>
                                    <Checkbox
                                        inputId="rpNoExpirationDate"
                                        checked={this.state.residencePermitDraft.noExpirationDate}
                                        onChange={(e) => this.onResidencePermitNoExpirationChange(!!e.checked)}
                                    />
                                    <label htmlFor="rpNoExpirationDate" style={{ margin: 0 }}>No expiration date</label>
                                </div>
                                {this.renderResidencePermitError("dateOfExpiry")}
                            </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            {/* CRM deactivates the previous permit only when the new row is active, so from an active
                                permit there is no way to "no active permit": a save there is always a replacement. The box
                                is free only for a first permit or when every recorded row is inactive. */}
                            <Checkbox
                                inputId="rpIsActive"
                                checked={this.state.residencePermitDraft.isActive}
                                disabled={this.state.residencePermit?.isActive === true}
                                onChange={(e) => this.onResidencePermitFieldChange("isActive", !!e.checked)}
                            />
                            <label htmlFor="rpIsActive" style={{ margin: 0 }}>Active</label>
                        </div>

                        <div>
                            <Label for="residencePermitReason" style={{ fontSize: "1em", whiteSpace: "nowrap" }}>
                                Reason
                            </Label>
                            <InputTextarea
                                value={this.state.residencePermitDraft.reason}
                                className={cx("form-control", this.state.residencePermitErrors.reason ? "p-invalid" : "")}
                                id="residencePermitReason"
                                placeholder={`Required, ${MIN_RESIDENCE_PERMIT_REASON_LENGTH}-${MAX_RESIDENCE_PERMIT_REASON_LENGTH} characters`}
                                maxLength={MAX_RESIDENCE_PERMIT_REASON_LENGTH}
                                rows={2}
                                style={{ width: "100%", resize: "vertical" }}
                                onChange={(e) => this.onResidencePermitFieldChange("reason", e.target.value)}
                            />
                            {this.renderResidencePermitError("reason")}
                        </div>

                        {this.state.isSlowSave && (
                            <small role="status" className="block" style={{ marginBottom: "8px" }}>
                                Still saving the residence permit…
                            </small>
                        )}
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                                label="Done"
                                type="button"
                                severity="success"
                                icon={<Check size={14} />}
                                loading={this.state.isSavingResidencePermit}
                                // Without the Issuing Country list a changed permit cannot pass validation; Cancel still closes.
                                disabled={this.state.isSavingResidencePermit || this.state.issuingCountries.length === 0}
                                tooltip={this.state.issuingCountries.length === 0 ? ISSUING_COUNTRIES_NOT_LOADED_TOOLTIP : undefined}
                                tooltipOptions={{ showOnDisabled: true, position: "top" }}
                                onClick={() => this.onDoneEditResidencePermit()}
                            />
                            <Button
                                label="Cancel"
                                type="button"
                                severity="secondary"
                                icon={<X size={14} />}
                                disabled={this.state.isSavingResidencePermit}
                                onClick={() => this.onCancelEditResidencePermit()}
                            />
                        </div>
                    </div>
                </Dialog>

                {this.state.showDiscardResidencePermitDialog && (
                    <Dialog
                        header="Discard residence permit changes?"
                        visible={true}
                        style={{ width: "40vw" }}
                        modal
                        onHide={() => this.setState({ showDiscardResidencePermitDialog: false })}
                    >
                        <div>
                            <Row>
                                <Col>
                                    <p style={{ marginBottom: "20px" }}>You have unsaved changes to the residence permit data. Discarding will revert them and clear the reason.</p>
                                </Col>
                            </Row>
                            <Row>
                                <Col style={{ display: "flex", gap: "8px" }}>
                                    <Button
                                        label="Discard changes"
                                        type="button"
                                        icon={<X size={16} />}
                                        severity="danger"
                                        onClick={() => this.onConfirmDiscardResidencePermit()}
                                    ></Button>
                                    <Button
                                        label="Keep editing"
                                        type="button"
                                        severity="secondary"
                                        onClick={() => this.setState({ showDiscardResidencePermitDialog: false })}
                                    ></Button>
                                </Col>
                            </Row>
                        </div>
                    </Dialog>
                )}
            </Fragment>
        );
    }
}
